/**
 * Copyright (c) 2025 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Server-side utility to generate window config for client-side runtime environment variables.
 * This allows injecting environment variables at runtime via a script tag in the HTML,
 * rather than bundling them at build time.
 */

declare global {
  interface Window {
    APP_CONFIG?: WindowConfig
  }
}

export interface WindowConfig {
  APP_URL?: string
  CAPTCHA_DISABLE?: boolean
  CAPTCHA_SITEKEY?: string
  DOPPLER_ENVIRONMENT?: string
  FACEBOOK_DOMAIN_VERIFICATION?: string
  GOOGLE_ANALYTICS?: string
  SENTRY_DISABLE?: boolean
  SENTRY_DSN_WEB?: string
  [key: string]: string | boolean | undefined
}

/**
 * Returns the app config object from the window object.
 * If the window object is not available, it returns the environment variables.
 */
export function getAppConfig(): WindowConfig {
  // Check if we are in the browser
  if (typeof window !== 'undefined' && window.APP_CONFIG) {
    return window.APP_CONFIG
  }

  // Fallback for Server-Side Rendering (SSR)
  // This allows the server to use process.env while the client uses window
  return getWindowConfig()
}

/**
 * Generates the window config object from environment variables.
 * Only include variables that should be exposed to the client.
 */
export function getWindowConfig(): WindowConfig {
  const config: WindowConfig = {}

  // Add runtime environment variables that should be available on the client
  config.APP_URL = process.env.APP_URL
  const captchaDisableEnv = process.env.CAPTCHA_DISABLE
  if (captchaDisableEnv !== undefined) {
    const lower = captchaDisableEnv.toLowerCase().trim()
    config.CAPTCHA_DISABLE = lower === 'true' || lower === '1' || lower === 'yes'
  } else {
    config.CAPTCHA_DISABLE = false
  }
  config.CAPTCHA_SITEKEY = process.env.CAPTCHA_SITEKEY
  config.DOPPLER_ENVIRONMENT = process.env.DOPPLER_ENVIRONMENT
  config.FACEBOOK_DOMAIN_VERIFICATION = process.env.FACEBOOK_DOMAIN_VERIFICATION
  config.GOOGLE_ANALYTICS = process.env.GOOGLE_ANALYTICS
  config.SENTRY_DSN_WEB = process.env.SENTRY_DSN_WEB
  // Convert string to boolean for SENTRY_DISABLE
  const sentryDisableEnv = process.env.SENTRY_DISABLE
  if (sentryDisableEnv !== undefined) {
    const lower = sentryDisableEnv.toLowerCase().trim()
    config.SENTRY_DISABLE = lower === 'true' || lower === '1' || lower === 'yes'
  } else {
    config.SENTRY_DISABLE = false
  }

  return config
}

/**
 * Generates the JavaScript content that injects the window config.
 * This should be used as inline script content in the head.
 */
export function generateWindowConfigScript(): string {
  const config = getWindowConfig()
  const configJson = JSON.stringify(config).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')

  return `window.APP_CONFIG = ${configJson};`
}
